import React, { Component, ReactNode, isValidElement } from "react";
import { UIError } from "@tajawal/web-core-sdk-v2/lib/utils/sentry.utils";
import ErrorPage from "@/app-common/components/ErrorPage";
import { UIErrorProps } from "@tajawal/web-core-sdk-v2/lib/types/sentry.types";
import { isProduction, isSSR } from "@/utils/compute.utils";
import { SourceMapConsumer } from "source-map";
import isFunction from "lodash/isFunction";

type ErrorBoundaryProps = {
  scope?: string;
  fallback?: ReactNode | React.FC<any>;
};

type ErrorBoundaryState = {
  hasError: boolean;
  error?: any;
  prevScope: string;
};
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  static defaultProps = {
    scope: "error-boundary",
  };

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, prevScope: props.scope };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  static getDerivedStateFromProps(props: ErrorBoundaryProps, state) {
    if (state.prevScope !== props.scope) {
      return {
        hasError: false,
        error: undefined,
        prevScope: props.scope,
      };
    }

    return state;
  }

  async componentDidCatch(error) {
    const RumGlobal = global.DD_RUM;
    UIError({ error, tag: "UI_ERROR" } as UIErrorProps);

    const stack = error?.stack?.split("\n") ?? [];
    const stackMatcher = stack?.[1]?.match(/(http.*\.js)/) || [];
    const sourceMapUrl = `${stackMatcher?.[0]}.map`;
    let originalPosition = null;

    if (stackMatcher.length > 0) {
      const response = await fetch(sourceMapUrl);
      const sourceMap = await response?.json();
      const consumer = await new SourceMapConsumer(sourceMap);
      const lineMatches = stack?.[1]?.match(/:(\d+):\d+/) || [];
      const columnMatches = stack?.[1]?.match(/:(\d+):(\d+)/) || [];

      originalPosition = consumer?.originalPositionFor({
        line: parseInt(lineMatches?.[1], 10),
        column: parseInt(columnMatches?.[2], 10),
      });
    }

    console.error(
      "UI_ERROR",
      JSON.stringify(
        {
          originalPosition,
          state: this.state,
        },
        undefined,
        2,
      ),
    );
    if (RumGlobal && isProduction() && !isSSR()) {
      isFunction(RumGlobal?.addError) &&
        RumGlobal?.addError?.(
          error,
          {
            scope: this.props?.scope,
            originalPosition,
          },
          "source",
        );
    }
  }

  render() {
    const { hasError, error } = this.state;
    const { fallback } = this.props;

    if (!hasError || !error) {
      return this.props.children;
    }

    if (isValidElement(fallback) || typeof fallback === "string") {
      return fallback;
    }
    if (typeof fallback === "function") {
      return fallback(error);
    }

    return <ErrorPage />;
  }
}

export default ErrorBoundary;
